import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, AlertCircle, CheckCircle, Clock, Package, ShieldCheck, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { warrantyService } from '../lib/warrantyService';
import { SoldProduct } from '../types';
import { formatDate } from '../lib/dateUtils';
import WarrantyClaimForm from '../components/warranties/WarrantyClaimForm';

export default function WarrantiesPage() {
  const navigate = useNavigate();
  const [soldProducts, setSoldProducts] = useState<SoldProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [filters, setFilters] = useState({
    productType: 'all' as 'all' | 'manufactured' | 'supply',
    warrantyStatus: 'all' as 'all' | 'active' | 'expired',
    startDate: '',
    endDate: '',
  });
  const [stats, setStats] = useState({
    totalActive: 0,
    totalExpired: 0,
    totalClaims: 0,
    pendingClaims: 0,
  });

  useEffect(() => {
    loadData();

    // Auto-refresh when page becomes visible (e.g., after editing a sales order)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [products, statsData] = await Promise.all([
        warrantyService.getSoldProducts(),
        warrantyService.getWarrantyStats(),
      ]);
      setSoldProducts(products);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading warranty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const searchFilters: any = {};

      if (searchTerm) {
        searchFilters.serialNumber = searchTerm;
        searchFilters.customerName = searchTerm;
      }

      if (filters.productType !== 'all') {
        searchFilters.productType = filters.productType;
      }

      if (filters.warrantyStatus !== 'all') {
        searchFilters.warrantyStatus = filters.warrantyStatus;
      }

      if (filters.startDate) {
        searchFilters.startDate = filters.startDate;
      }

      if (filters.endDate) {
        searchFilters.endDate = filters.endDate;
      }

      const products = await warrantyService.getSoldProducts(searchFilters);
      setSoldProducts(products);
    } catch (error) {
      console.error('Error searching warranties:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      productType: 'all',
      warrantyStatus: 'all',
      startDate: '',
      endDate: '',
    });
    loadData();
  };

  const getWarrantyStatus = (product: SoldProduct) => {
    const now = new Date();
    const warrantyEnd = new Date(product.warranty_end_date);
    return warrantyEnd >= now ? 'active' : 'expired';
  };

  const getStatusBadge = (product: SoldProduct) => {
    const status = getWarrantyStatus(product);
    if (status === 'active') {
      return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
    }
    return <Badge variant="secondary"><AlertCircle className="w-3 h-3 mr-1" />Expired</Badge>;
  };

  const filteredProducts = soldProducts.filter(product => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        product.serial_number.toLowerCase().includes(term) ||
        product.customer_name.toLowerCase().includes(term)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Warranties</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => loadData()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="secondary" onClick={() => setShowClaimForm(true)}>
            <AlertCircle className="w-4 h-4 mr-2" />
            New Warranty Claim
          </Button>
        </div>
      </div>

      {showClaimForm && (
        <WarrantyClaimForm
          onClose={() => setShowClaimForm(false)}
          onSuccess={() => {
            loadData();
            setShowClaimForm(false);
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Active Warranties</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.totalActive}</p>
            </div>
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Expired Warranties</p>
              <p className="text-2xl font-bold text-slate-600">{stats.totalExpired}</p>
            </div>
            <Clock className="w-8 h-8 text-slate-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Claims</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalClaims}</p>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Pending Claims</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pendingClaims}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-amber-600" />
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Search & Filter</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Input
              label="Serial Number or Customer"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
            />

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Product Type
              </label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.productType}
                onChange={(e) => setFilters({ ...filters, productType: e.target.value as any })}
              >
                <option value="all">All Types</option>
                <option value="manufactured">Manufactured</option>
                <option value="supply">Supply</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Warranty Status
              </label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.warrantyStatus}
                onChange={(e) => setFilters({ ...filters, warrantyStatus: e.target.value as any })}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <Input
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />

            <Input
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />

            <div className="flex items-end gap-2">
              <Button onClick={handleSearch} className="flex-1">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              <Button onClick={clearFilters} variant="secondary">
                Clear
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-600">Loading warranties...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-8 text-center text-slate-600">No warranties found</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-800 border-b-2 border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Serial Number
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Order Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Delivery Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Warranty End
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Claims
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-200 transition-colors duration-150 cursor-pointer">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm text-slate-900">
                        {product.serial_number}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">
                          {product.manufactured_product?.name || product.supply?.name || 'N/A'}
                        </p>
                        <Badge variant="secondary" className="mt-1">
                          {product.product_type}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900">
                      {product.customer_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 font-medium">
                      {formatDate(product.sale_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 font-medium">
                      {formatDate(product.warranty_start_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 font-medium">
                      {formatDate(product.warranty_end_date)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(product)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {product.warranty_claims && product.warranty_claims.length > 0 ? (
                        <Badge variant="warning">
                          {product.warranty_claims.length} claim{product.warranty_claims.length !== 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <span className="text-slate-700 font-medium">No claims</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/warranties/${product.id}`)}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
